import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiDownload, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { banksAPI } from '../services/api';

const Banks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);

  useEffect(() => { fetchBanks(); }, []);

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const res = await banksAPI.getBanks();
      setBanks(Array.isArray(res) ? res : res?.data || []);
    } catch (error) {
      toast.error('Failed to load banks');
      setBanks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bank?')) {
      try {
        await banksAPI.deleteBank(id);
        toast.success('Bank deleted successfully');
        fetchBanks();
      } catch (error) {
        toast.error('Failed to delete bank');
      }
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setShowModal(true);
  };

  const handleSave = async (bankData) => {
    try {
      if (editingBank) {
        await banksAPI.updateBank(editingBank._id, bankData);
        toast.success('Bank updated successfully');
      } else {
        await banksAPI.createBank(bankData);
        toast.success('Bank created successfully');
      }
      fetchBanks();
    } catch (error) {
      toast.error('Failed to save bank');
    }
  };

  const handleToggleStatus = async (bank) => {
    try {
      await banksAPI.toggleBankStatus(bank._id);
      toast.success('Status updated');
      fetchBanks();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const exportToExcel = () => {
    const filteredData = banks.filter(bank => bank.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const ws = XLSX.utils.json_to_sheet(filteredData.map(bank => ({
      Name: bank.name,
      Status: bank.status === 1 ? 'Active' : 'Inactive',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Banks');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'banks.xlsx');
    toast.success('Banks exported to Excel');
  };

  const exportToPDF = () => {
    const filteredData = banks.filter(bank => bank.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const doc = new jsPDF();
    doc.autoTable({
      head: [['Name', 'Status']],
      body: filteredData.map(bank => [
        bank.name,
        bank.status === 1 ? 'Active' : 'Inactive',
      ]),
      startY: 20,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [59, 130, 246] }
    });
    doc.save('banks.pdf');
    toast.success('Banks exported to PDF');
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <span className="font-medium text-gray-900">{row.name}</span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          {row.status === 1 ? (
            <FiToggleLeft className="w-4 h-4 text-green-500" />
          ) : (
            <FiToggleRight className="w-4 h-4 text-red-500" />
          )}
          <span className={row.status === 1 ? 'text-green-600' : 'text-red-600'}>
            {row.status === 1 ? 'Active' : 'Inactive'}
          </span>
        </div>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(row)}
            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
            title="Toggle Status"
          >
            {row.status === 1 ? <FiToggleLeft className="w-4 h-4" /> : <FiToggleRight className="w-4 h-4" />}
          </button>
        </div>
      ),
    },
  ];

  // Pagination logic
  const filteredBanks = banks.filter(bank => bank.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const paginatedData = filteredBanks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const customStyles = {
    table: { style: { backgroundColor: 'transparent' } },
    headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontWeight: '600' } },
    rows: { style: { backgroundColor: 'white', '&:hover': { backgroundColor: '#f8fafc' } } }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Banks Management</h1>
          <p className="text-gray-600 mt-1">Manage banks for account selection</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { setEditingBank(null); setShowModal(true); }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Bank</span>
          </button>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search banks..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchBanks}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center space-x-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          columns={columns}
          data={paginatedData}
          pagination
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          onChangeRowsPerPage={setPageSize}
          onChangePage={setCurrentPage}
          customStyles={customStyles}
          progressPending={loading}
          highlightOnHover
          pointerOnHover
          noHeader
        />
      </div>

      {/* Bank Modal */}
      <AnimatePresence>
        {showModal && (
          <BankModal
            bank={editingBank}
            onSave={data => {
              handleSave(data);
              setShowModal(false);
              setEditingBank(null);
            }}
            onClose={() => {
              setShowModal(false);
              setEditingBank(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const BankModal = ({ bank, onSave, onClose }) => {
  const [form, setForm] = useState(bank ? { name: bank.name, status: bank.status } : { name: '', status: 1 });

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === 'status' ? parseInt(value, 10) : value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {bank ? 'Edit Bank' : 'Add New Bank'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {bank ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Banks; 